import { useEffect, useRef, type DependencyList } from "react";

/**
 * Runs `effect` when `deps` change, but not on the first render.
 *
 * List pages use it to apply a filter dropdown the moment it changes: the
 * page's own mount effect already does the first load, so running here too
 * would fetch the same page twice.
 *
 * It compares against the deps it last saw rather than flipping a "mounted"
 * flag, so StrictMode's simulated remount (same deps) does not fire it either.
 */
export function useDidUpdate(effect: () => void, deps: DependencyList) {
  const prev = useRef<DependencyList | null>(null);
  useEffect(() => {
    const last = prev.current;
    prev.current = deps;
    if (last === null) return;
    if (last.length === deps.length && last.every((d, i) => Object.is(d, deps[i]))) return;
    effect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
